/**
 * LayoutManager - Centralized Layout System
 * Manages header and layout structure for all authenticated views
 */

import { t } from "../i18n/config";
import { renderLanguageToggle, setupLanguageToggle } from "../i18n/languageToggle";

export type NavPage = "home" | "leagues" | "players" | "standings" | string;

export interface LayoutConfig {
  activePage: NavPage;
  container: HTMLElement;
  content: string;
  title?: string;
  description?: string;
  showCreateLeague?: boolean;
}

/**
 * Renders the main header component for authenticated users
 * @param activePage - The currently active page for highlighting
 * @returns HTML string for the header
 */
export function renderHeader(activePage: NavPage): string {
  // The "Insights" link is for organizers only — the platform owner has its own
  // analytics inside the Platform Admin view and never sees this layout. Read the
  // role straight from localStorage (set at login) to avoid importing main.ts and
  // creating a circular dependency.
  const isOrganizer = localStorage.getItem("userRole") !== "owner";
  const insightsActive = activePage === "insights";

  const insightsDesktopLink = isOrganizer
    ? `
          <a
            href="#insights"
            class="nav-item text-sm font-medium transition-colors ${
              insightsActive ? "text-primary daylight:text-day-ink font-semibold" : "text-slate-600 dark:text-slate-400 hover:text-primary daylight:text-day-ink-soft daylight:hover:text-day-ink"
            }"
            data-page="insights"
          >
            ${t("nav.insights")}
          </a>`
    : "";

  const insightsMobileLink = isOrganizer
    ? `
          <a href="#insights" class="block px-4 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft hover:text-primary daylight:hover:text-day-ink hover:bg-slate-100 dark:hover:bg-slate-800 daylight:hover:bg-day-sunken transition">
            <span class="material-symbols-outlined align-middle mr-2">insights</span>
            ${t("nav.insights")}
          </a>`
    : "";

  return `
    <header class="sticky top-0 z-40 w-full border-b border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-md">
      <div class="max-w-7xl mx-auto px-6 lg:px-10 flex h-16 items-center justify-between">
        <!-- Logo & Brand -->
        <a href="#home" class="flex items-center gap-3 group hover:opacity-80 transition-opacity">
          <img src="/brand/icon-reverse.svg" alt="PadelPulse" class="size-10 hidden dark:block" />
          <img src="/brand/icon-color.svg" alt="PadelPulse" class="size-10 hidden daylight:block" />
          <h2 class="text-2xl leading-none text-slate-900 dark:text-white daylight:text-day-ink"><span class="font-medium">PADEL</span><span class="font-extrabold">PULSE</span></h2>
        </a>

        <!-- Navigation Menu -->
        <nav class="hidden md:flex flex-1 justify-center gap-8">
          <a 
            href="#home" 
            class="nav-item text-sm font-medium transition-colors ${
              activePage === "home" ? "text-primary daylight:text-day-ink font-semibold" : "text-slate-600 dark:text-slate-400 hover:text-primary daylight:text-day-ink-soft daylight:hover:text-day-ink"
            }"
            data-page="home"
          >
            ${t("nav.leagues")}
          </a>
          <a
            href="#players"
            class="nav-item text-sm font-medium transition-colors ${
              activePage === "players" ? "text-primary daylight:text-day-ink font-semibold" : "text-slate-600 dark:text-slate-400 hover:text-primary daylight:text-day-ink-soft daylight:hover:text-day-ink"
            }"
            data-page="players"
          >
            ${t("nav.players")}
          </a>${insightsDesktopLink}
        </nav>

        <!-- Right Actions -->
        <div class="flex items-center gap-3">
          <!-- Language Selector -->
          ${renderLanguageToggle()}

          <button id="create-league-header-btn" class="btn-primary hidden sm:flex">
            <span class="material-symbols-outlined">add</span>
            <span>${t("nav.newLeague")}</span>
          </button>

          <!-- Mobile Menu Toggle -->
          <button id="mobile-menu-toggle" class="md:hidden size-10 p-0 flex items-center justify-center border border-slate-400 daylight:border-day-ink-muted hover:bg-slate-100 dark:hover:bg-slate-800 daylight:hover:bg-day-sunken rounded-lg transition">
            <span class="material-symbols-outlined">menu</span>
          </button>

          <!-- Account Button -->
          <a href="#account" id="account-header-btn" class="btn-secondary size-10 p-0 flex items-center justify-center border border-slate-400 daylight:border-day-ink-muted transition" title="${t("nav.account")}">
            <span class="material-symbols-outlined">account_circle</span>
          </a>

          <!-- Logout Button -->
          <button id="logout-header-btn" class="btn-secondary size-10 p-0 flex items-center justify-center border border-slate-400 daylight:border-day-ink-muted transition" title="${t("nav.logout")}">
            <span class="material-symbols-outlined">logout</span>
          </button>
        </div>
      </div>

      <!-- Mobile Menu (hidden by default) -->
      <nav id="mobile-nav" class="hidden md:hidden border-t border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 bg-background-light dark:bg-background-dark">
        <div class="px-6 py-4 space-y-2">
          <a href="#home" class="block px-4 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft hover:text-primary daylight:hover:text-day-ink hover:bg-slate-100 dark:hover:bg-slate-800 daylight:hover:bg-day-sunken transition">
            <span class="material-symbols-outlined align-middle mr-2">home</span>
            ${t("nav.leagues")}
          </a>
          <a href="#players" class="block px-4 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft hover:text-primary daylight:hover:text-day-ink hover:bg-slate-100 dark:hover:bg-slate-800 daylight:hover:bg-day-sunken transition">
            <span class="material-symbols-outlined align-middle mr-2">groups</span>
            ${t("nav.players")}
          </a>${insightsMobileLink}
          <a href="#account" class="block px-4 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft hover:text-primary daylight:hover:text-day-ink hover:bg-slate-100 dark:hover:bg-slate-800 daylight:hover:bg-day-sunken transition">
            <span class="material-symbols-outlined align-middle mr-2">account_circle</span>
            ${t("nav.account")}
          </a>
        </div>
      </nav>
    </header>
  `;
}

/**
 * Wraps content in the authenticated base layout
 * Includes sticky header, main container, and proper spacing
 * @param config - Configuration for the layout
 * @returns Complete HTML for the page
 */
export function applyBaseLayout(config: LayoutConfig): string {
  const header = renderHeader(config.activePage);

  return `
    <div class="flex flex-col h-screen overflow-hidden bg-background-light dark:bg-background-dark">
      ${header}
      
      <!-- Main Content Area -->
      <main class="flex-1 overflow-y-auto">
        <div class="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          ${config.content}
        </div>
      </main>
    </div>
  `;
}

/**
 * Setup event listeners for header actions
 * Call this in your view's setupEventListeners method
 */
export function setupHeaderListeners(
  container: HTMLElement,
  handlers: {
    onCreateLeague?: () => void;
    onLogout?: () => void;
    onNavigation?: (page: NavPage) => void;
  }
): void {
  // Logout button
  const logoutBtn = container.querySelector("#logout-header-btn");
  logoutBtn?.addEventListener("click", () => {
    localStorage.removeItem("authToken");
    if (handlers.onLogout) {
      handlers.onLogout();
    } else {
      window.location.hash = "#login";
    }
  });

  // Create League button
  const createBtn = container.querySelector("#create-league-header-btn");
  createBtn?.addEventListener("click", () => {
    if (handlers.onCreateLeague) {
      handlers.onCreateLeague();
    }
  });

  // Mobile menu toggle
  const mobileMenuToggle = container.querySelector("#mobile-menu-toggle");
  const mobileNav = container.querySelector("#mobile-nav");
  mobileMenuToggle?.addEventListener("click", () => {
    mobileNav?.classList.toggle("hidden");
  });

  // Close mobile menu when navigation link is clicked
  const mobileNavLinks = container.querySelectorAll("#mobile-nav a");
  mobileNavLinks.forEach(link => {
    link.addEventListener("click", () => {
      mobileNav?.classList.add("hidden");
    });
  });

  // Language toggle: switching locale re-renders the active view via the
  // locale-changed listener registered in main.ts.
  setupLanguageToggle(container);
}